import { captureMessage } from '@sentry/vue';
import { OUTFIT_CATEGORY_KEY } from '@/constants';
import { FlowModeEnum } from '@/enum/flow_mode';
import { ExitDialogTypeEnum } from '@/enum/exit_dialog_type';
import { GenderEnum } from '@/enum/gender';
import { StyleEnum } from '@/enum/style';
import { CategoryAssetTypeEnum } from '@/enum/category_asset_types';
import CategoryAssets from '@/services/CategoryAssets';
import FeatureGating from '@/services/FeatureGating';
import ClientBuild from '@/services/ClientBuild';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';

const CREATE_FLOW_INITIAL_CATEGORY: string = FeatureGating.isSkinToneSwatchEnabled()
  ? 'skin_tone_swatch'
  : 'skin_tone';
const EDIT_FLOW_INITIAL_CATEGORY: string = 'hair';
const DEFAULT_INITIAL_SECTION_ID = null; // This corresponds to the top section (i.e., no scrolling).
const FORCE_SHOW_EXIT_DIALOG_PARAM: string = 'force_show_exit_dialog';
const EXIT_DIALOG_TYPE_PARAM: string = 'exit_dialog_type';

interface QueryParams {
  style?: StyleEnum;
  flow_mode?: FlowModeEnum;
  gender?: GenderEnum;
  is_3d_mode?: boolean;
}

interface EntryPoint {
  goTo: { category: string; sectionId?: number };
}

interface LaunchConfig {
  initialTraits: Record<string, number>;
  flowMode: FlowModeEnum;
  categoryIndex: number;
  initialSectionId: number | null;
  centerActiveCategoryIcon: boolean;
  assetOverrides: string[];
  is3dMode: boolean;
  forceShowExitDialog: boolean;
  exitDialogType: ExitDialogTypeEnum;
}

// exported for unit tests
export function parseQueryString(queryString: string): QueryParams {
  const params: Record<string, string> = {};
  if (!queryString) {
    return params;
  }
  for (const [key, value] of queryString.split('&').map((keyValue) => keyValue.split('='))) {
    if (key) {
      params[key] = value;
    }
  }
  return params;
}

const seedDefaultQueryParams = (params: QueryParams): void => {
  if (!params.gender) {
    params.gender = 1;
  }
  if (!params.style) {
    params.style = 5;
  }
  if (!params.flow_mode) {
    params.flow_mode = FlowModeEnum.CREATE;
  }
};

const parseQueryParams = (params: QueryParams) => {
  const initialTraits: Record<string, number> = {};
  let flowMode: FlowModeEnum | null = null;
  let featureGating;
  let entryPoint: EntryPoint | null = null;
  let assetOverrides: string[] = [];
  let is3dMode = false;
  let forceShowExitDialog = false;
  let exitDialogType: ExitDialogTypeEnum = ExitDialogTypeEnum.DEFAULT;

  // for backward compatibility, assume the absence of snapchat_build param in request url
  // indicate that it is from snapchat app
  let clientBuild: ClientBuild = { app: 'snapchat' };
  for (const [key, value] of Object.entries(params)) {
    if (key === 'flow_mode') {
      flowMode = value.toLocaleLowerCase();
      if (flowMode && !Object.values(FlowModeEnum).includes(flowMode)) {
        throw new Error('Incorrect flow mode: ' + flowMode);
      }
    } else if (key === 'feature_gating') {
      featureGating = JSON.parse(decodeURIComponent(value));
    } else if (key === 'client') {
      clientBuild = JSON.parse(decodeURIComponent(value));
    } else if (key === 'entry_point') {
      try {
        // entry_point should be a base64-encoded JSON object following entryPointSchema
        entryPoint = getEntryPointFromQueryParam(value);
      } catch (e) {
        captureMessage('[GetLaunchConfig] Invalid value of entryPoint parameter: ' + value);
      }
    } else if (key === 'asset_overrides') {
      const overrides = decodeURIComponent(value).split(',');
      for (const overrideStr of overrides) {
        if (overrideStr in CategoryAssetTypeEnum) {
          assetOverrides.push(overrideStr);
        }
      }
    } else if (key === 'is_3d_mode') {
      is3dMode = BUILD_FLAGS.ENVIRONMENT !== 'production' && value === 'true';
    } else if (key === FORCE_SHOW_EXIT_DIALOG_PARAM) {
      // Setting this query param is optional. If set to true, the exit dialog will show when exiting the builder,
      // even if the user has not modified their avatar.
      forceShowExitDialog = value;
    } else if (key === EXIT_DIALOG_TYPE_PARAM) {
      exitDialogType = value;
      if (exitDialogType && !Object.values(ExitDialogTypeEnum).includes(exitDialogType)) {
        throw new Error('Incorrect exit dialog type: ' + exitDialogType);
      }
    } else {
      if (key === 'gender' && !Object.values(GenderEnum).includes(parseInt(value))) {
        throw new Error(`Invalid gender parameter: ${value}`);
      } else if (key === 'style' && !Object.values(StyleEnum).includes(parseInt(value))) {
        throw new Error(`Invalid style parameter: ${value}`);
      }
      initialTraits[key] = parseInt(value);
    }
  }

  if (!flowMode) {
    throw Error(`[GetLaunchConfig] flowMode is not defined: ${flowMode}`);
  }

  return {
    initialTraits,
    flowMode,
    featureGating,
    clientBuild,
    entryPoint,
    assetOverrides,
    is3dMode,
    forceShowExitDialog,
    exitDialogType,
  };
};

// Visible for testing

export const validateEntryPoint = (entryPoint: EntryPoint) => {
  if (!entryPoint || !entryPoint.goTo) {
    return false;
  }

  const goTo = entryPoint.goTo;
  // required entryPoint.category string
  if (!goTo.category || typeof goTo.category !== 'string') {
    return false;
  }

  // optional goTo.sectionId integer
  if (goTo.sectionId) {
    return (
      typeof goTo.sectionId === 'number' &&
      isFinite(goTo.sectionId) &&
      Math.floor(goTo.sectionId) === goTo.sectionId
    );
  }
  return true;
};

// Visible for testing.
export const getEntryPointFromQueryParam = (entryPointString: string): EntryPoint | null => {
  try {
    let entryPoint = JSON.parse(atob(decodeURIComponent(entryPointString)));
    // Validate JSON object against the schema.
    if (entryPoint && !validateEntryPoint(entryPoint)) {
      entryPoint = null;
    }
    return entryPoint;
  } catch (e) {
    captureMessage('[GetLaunchConfig] Failed to decode entry point: ' + entryPointString);
    // When additional attributes are appended to the end of the deeplink url, entry_point query param is parsed
    // incorrectly by clients (e.g., [base64_entry_point]%attr1?attr2 ).
    // Try to isolate the entry point query param string from attributes by splitting entryPointString into
    // substrings using delimiters "?" or "%", and taking the first substring.
    const chunks = entryPointString.split(/\?|%/);
    return JSON.parse(atob(decodeURIComponent(chunks[0])));
  }
};

// Visible for testing.
export function getInitialCategory(
  gender: GenderEnum,
  style: StyleEnum,
  flowMode: FlowModeEnum,
  entryPoint: EntryPoint | null,
): string {
  const category = entryPoint && entryPoint.goTo ? entryPoint.goTo.category : null;
  const flatCategoryList = CategoryAssets.getFlatCategoryList(gender, style, flowMode);
  if (category && typeof category === 'string' && flatCategoryList.includes(category)) {
    return category;
  }
  switch (flowMode) {
    case FlowModeEnum.CREATE:
      return CREATE_FLOW_INITIAL_CATEGORY;
    case FlowModeEnum.EDIT:
      return EDIT_FLOW_INITIAL_CATEGORY;
    case FlowModeEnum.OUTFIT:
      return OUTFIT_CATEGORY_KEY;
    case FlowModeEnum.PROFILE:
      return EDIT_FLOW_INITIAL_CATEGORY;
    default:
      throw new Error('Incorrect flow mode: ' + flowMode);
  }
}

const getInitialCategoryIndex = (
  { gender, style }: Record<string, number>,
  flowMode: FlowModeEnum,
  entryPoint: EntryPoint | null,
): number => {
  const initialCategory: string = getInitialCategory(gender, style, flowMode, entryPoint);
  const categoryIndex = CategoryAssets.getCategoryIndex(initialCategory, gender, style, flowMode);
  if (categoryIndex === -1) {
    captureMessage(
      `[GetLaunchConfig] Invalid initialCategory: ${gender}, ${style}, ${flowMode}, ${entryPoint}`,
    );
    return 0;
  }
  return categoryIndex;
};

// Visible for testing
export function getInitialSectionId(entryPoint: EntryPoint | null): number | null {
  // Assume that only outfit and mix and match categories have sections.
  const category = entryPoint && entryPoint.goTo ? entryPoint.goTo.category : null;

  const sectionId =
    entryPoint && entryPoint.goTo && entryPoint.goTo.sectionId !== undefined
      ? entryPoint.goTo.sectionId
      : null;
  if (
    category === OUTFIT_CATEGORY_KEY ||
    (category && DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(category))
  ) {
    return sectionId;
  } else if (sectionId !== null) {
    captureMessage(
      `[GetLaunchConfig] sectionId ${sectionId} was specified for a non-outfit category: ${category}`,
    );
  }
  return DEFAULT_INITIAL_SECTION_ID;
}

const shouldCenterInitialCategoryIcon = (entryPoint: EntryPoint | null): boolean => {
  return entryPoint && entryPoint.goTo ? true : false;
};

export function getLaunchConfig(launchUrl: Location): LaunchConfig {
  const queryString: string = launchUrl.search.substring(1); // the part after '?'
  const params: QueryParams = parseQueryString(queryString);

  if (BUILD_FLAGS.ENVIRONMENT !== 'production') {
    seedDefaultQueryParams(params);
  }

  const queryParamConfig = parseQueryParams(params);

  if (queryParamConfig.featureGating) {
    FeatureGating.setFeatureGating(queryParamConfig.featureGating);
  }

  ClientBuild.setClientBuild(
    queryParamConfig.clientBuild.app,
    queryParamConfig.clientBuild.app_version,
    queryParamConfig.clientBuild?.safe_area_insets,
  );

  return {
    initialTraits: queryParamConfig.initialTraits,
    flowMode: queryParamConfig.flowMode,
    categoryIndex: getInitialCategoryIndex(
      queryParamConfig.initialTraits,
      queryParamConfig.flowMode,
      queryParamConfig.entryPoint,
    ),
    initialSectionId: getInitialSectionId(queryParamConfig.entryPoint),
    centerActiveCategoryIcon: shouldCenterInitialCategoryIcon(queryParamConfig.entryPoint),
    assetOverrides: queryParamConfig.assetOverrides,
    is3dMode: queryParamConfig.is3dMode,
    forceShowExitDialog: queryParamConfig.forceShowExitDialog,
    exitDialogType: queryParamConfig.exitDialogType,
  };
}
